import { memo } from 'react';

function MarketComparisonPanel({ position }) {
  
  // Comprehensive validation
  if (!position || !position.salary) {
    return (
      <div className="text-center py-4 text-gray-500">
        <p>Keine Marktdaten verfügbar</p>
      </div>
    );
  }
  
  // Werte aus dem salary-Objekt extrahieren (Compensation Partner) mit Fallback-Werten
  const {
    medianBasic = 0, 
    medianTotalCash = 0,
    peerigonCurrentBasic = 0, 
    peerigonCurrentTotalCash = 0,
    basicPercentage = 0,
    totalCashPercentage = 0,
    medianBasicMonthlyToIncrease = 0
  } = position.salary || {};
  
  // Extract market data from other sources - prioritize experience level data for Kununu
  const kununuSalary = position.kununu?.derivedSalary?.derivedSalary || position.kununu?.averageSalary;
  const kununuDataPoints = position.kununu?.dataPoints;
  const kununuRange = position.kununu?.salaryRange;
  const kununuExperienceLevel = position.kununu?.config?.experienceLevel;
  const kununuHasManagement = position.kununu?.config?.hasManagement;
  const kununuDataSource = position.kununu?.derivedSalary?.dataSource;
  const kununuUsesByResponsibility = kununuDataSource === 'experience_and_management';
  
  const gehaltDeSalary = position.gehaltDe?.derivedSalary?.derivedSalary || position.gehaltDe?.averageSalary;
  const gehaltDeDataPoints = position.gehaltDe?.dataPoints;
  const gehaltDeRange = position.gehaltDe?.salaryRange;
  
  // Extract CP job selection details
  const cpJobSelections = position.config?.stellen || [];
  
  // Helper function to format experience level
  function formatExperienceLevel(level) {
    const levelMapping = {
      'entryLevel': 'Einsteiger (< 3 Jahre)',
      'midLevel': 'Erfahren (3-6 Jahre)', 
      'seniorLevel': 'Senior (6-10 Jahre)',
      'expertLevel': 'Experte (10+ Jahre)'
    };
    return levelMapping[level] || level;
  }
  
  // Calculate percentages vs market sources (using Total Cash for fair comparison) with division safety
  const kununuPercentage = (kununuSalary && kununuSalary > 0 && peerigonCurrentTotalCash > 0) 
    ? ((peerigonCurrentTotalCash / kununuSalary) - 1) 
    : null;
  const gehaltDePercentage = (gehaltDeSalary && gehaltDeSalary > 0 && peerigonCurrentTotalCash > 0) 
    ? ((peerigonCurrentTotalCash / gehaltDeSalary) - 1) 
    : null;
  
  return (
    <div className="bg-white p-4 rounded-lg shadow mb-4">
      <h3 className="text-lg font-semibold mb-4">Marktvergleich: {position.positionsBezeichnung}</h3>
      
      {/* Comparison Note */}
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
        <p className="text-sm text-blue-800">
          <strong>💡 Vergleichsmethodik:</strong> Kununu und Gehalt.de Daten werden als &ldquo;Total Cash&rdquo; interpretiert und mit unserem Total Cash verglichen. 
          CP zeigt sowohl Grundgehalt als auch Total Cash für vollständige Transparenz.
          {kununuUsesByResponsibility && (
            <span className="block mt-1">
              <strong>✓</strong> Kununu nutzt präzise &ldquo;byResponsibility&rdquo; Daten (Erfahrungslevel + Führungsverantwortung).
            </span>
          )}
        </p>
      </div>
      
      {/* CP Job Selection Details */}
      {cpJobSelections.length > 0 && (
        <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-md">
          <h4 className="text-sm font-medium text-indigo-800 mb-2">🎯 Compensation Partner Stellenauswahl</h4>
          <div className="flex flex-wrap gap-2">
            {cpJobSelections.map((stelle, index) => (
              <span key={index} className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-md font-medium">
                {stelle.selectJob}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {/* Experience Level Information */}
      {(kununuExperienceLevel || position.gehaltDe?.config?.experienceLevel) && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
          <h4 className="text-sm font-medium text-green-800 mb-2">📊 Erfahrungslevel-Mapping</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            {kununuExperienceLevel && (
              <div className="text-green-700">
                <span className="font-medium">Kununu:</span> {formatExperienceLevel(kununuExperienceLevel)}
                {kununuHasManagement !== undefined && (
                  <span className="ml-1">({kununuHasManagement ? 'mit' : 'ohne'} Führungsverantwortung)</span>
                )}
                {kununuUsesByResponsibility && (
                  <span className="ml-2 text-green-600 font-medium">✓ byResponsibility</span>
                )}
              </div>
            )}
            {position.gehaltDe?.config?.experienceLevel && (
              <div className="text-green-700">
                <span className="font-medium">Gehalt.de:</span> {formatExperienceLevel(position.gehaltDe.config.experienceLevel)}
                {position.gehaltDe.config.hasManagement !== undefined && (
                  <span className="ml-1">({position.gehaltDe.config.hasManagement ? 'mit' : 'ohne'} Führungsverantwortung)</span>
                )}
              </div>
            )}
          </div>
          {kununuUsesByResponsibility && (
            <div className="mt-2 text-xs text-green-600">
              <strong>ℹ️ byResponsibility:</strong> Die zuverlässigsten Kununu-Daten, die sowohl Erfahrungslevel als auch Führungsverantwortung berücksichtigen.
            </div>
          )}
        </div>
      )}
      
      {/* Enhanced Multi-Source Comparison Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quelle</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Markt-Median</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Markt-Spanne</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ist-Gehalt</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Abweichung</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datenpunkte</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {/* Compensation Partner Basic Row */}
            <tr>
              <td className="px-6 py-4 whitespace-nowrap font-medium text-blue-600">CP (Grundgehalt)</td>
              <td className="px-6 py-4 whitespace-nowrap">{medianBasic.toLocaleString()} €</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {(medianBasic * 0.8).toLocaleString()} - {(medianBasic * 1.2).toLocaleString()} €
              </td>
              <td className="px-6 py-4 whitespace-nowrap font-medium">{peerigonCurrentBasic.toLocaleString()} €</td>
              <td className={`px-6 py-4 whitespace-nowrap font-medium ${basicPercentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {basicPercentage >= 0 ? '+' : ''}{Math.round(basicPercentage * 100)}%
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Marktdaten</td>
            </tr>
            
            {/* Compensation Partner Total Cash Row */}
            <tr className="bg-blue-50">
              <td className="px-6 py-4 whitespace-nowrap font-medium text-blue-700">CP (Total Cash)</td>
              <td className="px-6 py-4 whitespace-nowrap font-medium">{medianTotalCash.toLocaleString()} €</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {(medianTotalCash * 0.8).toLocaleString()} - {(medianTotalCash * 1.2).toLocaleString()} €
              </td>
              <td className="px-6 py-4 whitespace-nowrap font-bold">{peerigonCurrentTotalCash.toLocaleString()} €</td>
              <td className={`px-6 py-4 whitespace-nowrap font-bold ${totalCashPercentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {totalCashPercentage >= 0 ? '+' : ''}{Math.round(totalCashPercentage * 100)}%
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">📊 Vergleichsbasis</td>
            </tr>
            
            {/* Kununu Row */}
            {kununuSalary ? (
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-orange-600">
                  Kununu (Total)
                  {kununuExperienceLevel && (
                    <div className="text-xs text-orange-500 font-normal">
                      {formatExperienceLevel(kununuExperienceLevel).split('(')[0].trim()}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{Math.round(kununuSalary).toLocaleString()} €</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {kununuRange ? `${kununuRange.min.toLocaleString()} - ${kununuRange.max.toLocaleString()} €` : 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap font-medium">{peerigonCurrentTotalCash.toLocaleString()} €</td>
                <td className={`px-6 py-4 whitespace-nowrap font-medium ${kununuPercentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {kununuPercentage >= 0 ? '+' : ''}{Math.round(kununuPercentage * 100)}%
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {kununuDataPoints ? `${kununuDataPoints} Berichte` : 'N/A'}
                  {kununuUsesByResponsibility && (
                    <div className="text-xs text-green-600 font-medium">
                      ✓ Präzise Daten
                    </div>
                  )}
                  {kununuDataSource && kununuDataSource !== 'experience_and_management' && (
                    <div className="text-xs text-gray-400">
                      {kununuDataSource === 'experience_only' ? 'Nur Erfahrung' : 'Durchschnitt'}
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-400">Kununu (Total)</td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-400" colSpan="5">Keine Daten verfügbar</td>
              </tr>
            )}
            
            {/* Gehalt.de Row */}
            {gehaltDeSalary ? (
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-pink-600">
                  Gehalt.de (Total)
                  {position.gehaltDe?.config?.experienceLevel && (
                    <div className="text-xs text-pink-500 font-normal">
                      {formatExperienceLevel(position.gehaltDe.config.experienceLevel).split('(')[0].trim()}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{Math.round(gehaltDeSalary).toLocaleString()} €</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {gehaltDeRange ? `${gehaltDeRange.min.toLocaleString()} - ${gehaltDeRange.max.toLocaleString()} €` : 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap font-medium">{peerigonCurrentTotalCash.toLocaleString()} €</td>
                <td className={`px-6 py-4 whitespace-nowrap font-medium ${gehaltDePercentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {gehaltDePercentage >= 0 ? '+' : ''}{Math.round(gehaltDePercentage * 100)}%
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {gehaltDeDataPoints ? `${gehaltDeDataPoints} Berichte` : 'N/A'}
                  {position.gehaltDe?.derivedSalary?.dataSource && (
                    <div className="text-xs text-gray-400">
                      {position.gehaltDe.derivedSalary.dataSource}
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-400">Gehalt.de (Total)</td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-400" colSpan="5">Keine Daten verfügbar</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Visual Market Position Indicator */}
      <div className="mt-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Total Cash Position (CP Vergleich)</h4>
        <div className="relative">
          <div className="flex items-center mb-2">
            <div className="w-full bg-gray-200 rounded-full h-4 relative">
              <div className="bg-blue-600 h-4 rounded-full transition-all duration-300" 
                style={{ width: `${Math.min(100, Math.max(0, ((peerigonCurrentTotalCash - medianTotalCash * 0.8) / (medianTotalCash * 0.4)) * 100))}%` }}></div>
              {/* Current position marker */}
              <div className="absolute top-0 h-4 w-1 bg-gray-900 rounded" 
                style={{ left: `${Math.min(100, Math.max(0, ((peerigonCurrentTotalCash - medianTotalCash * 0.8) / (medianTotalCash * 0.4)) * 100))}%` }}></div>
            </div>
          </div>
          <div className="flex justify-between text-xs text-gray-600">
            <span>Unteres Quartil<br/>{(medianTotalCash * 0.8).toLocaleString()} €</span>
            <span>Median<br/>{medianTotalCash.toLocaleString()} €</span>
            <span>Oberes Quartil<br/>{(medianTotalCash * 1.2).toLocaleString()} €</span>
          </div>
        </div>
      </div>
      
      {/* Compensation Partner Adjustment Recommendation */}
      {medianBasicMonthlyToIncrease > 0 && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="font-medium text-yellow-800">💡 Gehaltsanpassung (Compensation Partner):</p>
          <p>Monatlich: <span className="font-medium">{medianBasicMonthlyToIncrease} €</span></p>
          <p>Jährlich: <span className="font-medium">{(medianBasicMonthlyToIncrease * 12).toLocaleString()} €</span></p>
        </div>
      )}
    </div>
  );
}

export default memo(MarketComparisonPanel);